import { BaseService } from "../api/BaseService";
import type { FinalResponse } from "../models";
import type {
  CreateCommentRequest,
  CreateCommentResponse,
  EditCommentRequest,
  EditCommentResponse,
  DeleteCommentResponse,
  ListCommentsRequest,
  ListCommentsResponse,
  CreateLikeRequest,
  CreateLikeResponse,
  RemoveLikeRequest,
  RemoveLikeResponse,
  CountLikesRequest,
  CountLikesResponse,
  CreateShareRequest,
  CreateShareResponse,
  CountSharesRequest,
  CountSharesResponse,
  CreatePostRequest,
  CreatePostResponse,
  EditPostRequest,
  EditPostResponse,
  DeletePostResponse,
  ListPostsByUserRequest,
  ListPostsByUserResponse,
  GetPostResponse,
  CreateUserProfileRequest,
  UserProfileResponse,
  UpdateUserProfileRequest,
  UpdateDisplayNameRequest,
  ListUserProfilesRequest,
  ListUserProfilesResponse,
  FollowRequest,
  FollowResponse,
  UnfollowRequest,
  UnfollowResponse,
  ListFollowersResponse,
  ListFollowingResponse,
  GetFriendsEventsResponse,
  CountFollowersResponse,
  CountFollowedResponse,
  CountPostsResponse,
  ViewedContentRequest,
  ClickContentRequest,
  OpenedContentRequest,
  CreateSocialUserRequest,
  CreateUserResponse,
} from "../models/social-interaction";

export class SocialInteractionService extends BaseService {
  private static readonly SERVICE = "Social-Interaction";

  // ==================== User Management ====================

  async createSocialUser(
    data: CreateSocialUserRequest,
  ): Promise<CreateUserResponse> {
    const res = await this.api.request<FinalResponse<CreateUserResponse>>(
      "/social-interaction/user",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  // ==================== Follow/Unfollow ====================

  async followUser(data: FollowRequest): Promise<FollowResponse> {
    const res = await this.api.request<FinalResponse<FollowResponse>>(
      `/social-interaction/user/${data.followerID}/follow/${data.followeeID}`,
      {
        method: "POST",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async unfollowUser(data: UnfollowRequest): Promise<UnfollowResponse> {
    const res = await this.api.request<FinalResponse<UnfollowResponse>>(
      `/social-interaction/user/${data.followerID}/follow/${data.followeeID}`,
      {
        method: "DELETE",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async getFollowers(userId: string): Promise<ListFollowersResponse> {
    const res = await this.api.request<FinalResponse<ListFollowersResponse>>(
      `/social-interaction/user/${userId}/followers`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async getFollowing(userId: string): Promise<ListFollowingResponse> {
    const res = await this.api.request<FinalResponse<ListFollowingResponse>>(
      `/social-interaction/user/${userId}/following`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async getFriendsEvents(userId: string): Promise<GetFriendsEventsResponse> {
    const res = await this.api.request<FinalResponse<GetFriendsEventsResponse>>(
      `/social-interaction/user/${userId}/friends/events`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async countFollowers(userId: string): Promise<CountFollowersResponse> {
    const res = await this.api.request<FinalResponse<CountFollowersResponse>>(
      `/social-interaction/user/${userId}/followers/count`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async countFollowing(userId: string): Promise<CountFollowedResponse> {
    const res = await this.api.request<FinalResponse<CountFollowedResponse>>(
      `/social-interaction/user/${userId}/following/count`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  // ==================== Comments ====================

  async createComment(
    data: CreateCommentRequest,
  ): Promise<CreateCommentResponse> {
    const res = await this.api.request<FinalResponse<CreateCommentResponse>>(
      "/social-interaction/comments",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async editComment(
    commentId: string,
    data: EditCommentRequest,
  ): Promise<EditCommentResponse> {
    const res = await this.api.request<FinalResponse<EditCommentResponse>>(
      `/social-interaction/comments/${commentId}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async deleteComment(commentId: string): Promise<DeleteCommentResponse> {
    const res = await this.api.request<FinalResponse<DeleteCommentResponse>>(
      `/social-interaction/comments/${commentId}`,
      {
        method: "DELETE",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async getComment(commentId: string): Promise<CreateCommentResponse> {
    const res = await this.api.request<FinalResponse<CreateCommentResponse>>(
      `/social-interaction/comments/${commentId}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async listComments(
    request: ListCommentsRequest,
  ): Promise<ListCommentsResponse> {
    const params = new URLSearchParams();
    if (request.limit !== undefined)
      params.append("limit", request.limit.toString());
    if (request.offset !== undefined)
      params.append("offset", request.offset.toString());

    const res = await this.api.request<FinalResponse<ListCommentsResponse>>(
      `/social-interaction/comments/type/${request.elementType}/id/${request.elementID}?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  // ==================== Likes ====================

  async createLike(data: CreateLikeRequest): Promise<CreateLikeResponse> {
    const res = await this.api.request<FinalResponse<CreateLikeResponse>>(
      "/social-interaction/likes",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async removeLike(data: RemoveLikeRequest): Promise<RemoveLikeResponse> {
    const res = await this.api.request<FinalResponse<RemoveLikeResponse>>(
      "/social-interaction/likes",
      {
        method: "DELETE",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async countLikes(request: CountLikesRequest): Promise<CountLikesResponse> {
    const res = await this.api.request<FinalResponse<CountLikesResponse>>(
      `/social-interaction/likes/type/${request.elementType}/id/${request.elementID}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  // ==================== Shares ====================

  async createShare(data: CreateShareRequest): Promise<CreateShareResponse> {
    const res = await this.api.request<FinalResponse<CreateShareResponse>>(
      "/social-interaction/shares",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async countShares(request: CountSharesRequest): Promise<CountSharesResponse> {
    const res = await this.api.request<FinalResponse<CountSharesResponse>>(
      `/social-interaction/shares/type/${request.elementType}/id/${request.elementID}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  // ==================== Posts ====================

  async createPost(data: CreatePostRequest): Promise<CreatePostResponse> {
    const res = await this.api.request<FinalResponse<CreatePostResponse>>(
      "/social-interaction/posts",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async editPost(
    postId: string,
    data: EditPostRequest,
  ): Promise<EditPostResponse> {
    const res = await this.api.request<FinalResponse<EditPostResponse>>(
      `/social-interaction/posts/${postId}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async deletePost(postId: string): Promise<DeletePostResponse> {
    const res = await this.api.request<FinalResponse<DeletePostResponse>>(
      `/social-interaction/posts/${postId}`,
      {
        method: "DELETE",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async getPost(postId: string): Promise<GetPostResponse> {
    const res = await this.api.request<FinalResponse<GetPostResponse>>(
      `/social-interaction/posts/${postId}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async listPostsByUser(
    request: ListPostsByUserRequest,
  ): Promise<ListPostsByUserResponse> {
    const params = new URLSearchParams();
    if (request.limit !== undefined)
      params.append("limit", request.limit.toString());
    if (request.offset !== undefined)
      params.append("offset", request.offset.toString());

    const res = await this.api.request<FinalResponse<ListPostsByUserResponse>>(
      `/social-interaction/user/${request.userID}/posts?${params}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async countPosts(userId: string): Promise<CountPostsResponse> {
    const res = await this.api.request<FinalResponse<CountPostsResponse>>(
      `/social-interaction/user/${userId}/posts/count`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  // ==================== User Profiles ====================

  async createUserProfile(
    data: CreateUserProfileRequest,
  ): Promise<UserProfileResponse> {
    const res = await this.api.request<FinalResponse<UserProfileResponse>>(
      "/social-interaction/profiles",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async getUserProfileById(profileId: string): Promise<UserProfileResponse> {
    const res = await this.api.request<FinalResponse<UserProfileResponse>>(
      `/social-interaction/profiles/${profileId}`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async getUserProfileByUserId(userId: string): Promise<UserProfileResponse> {
    const res = await this.api.request<FinalResponse<UserProfileResponse>>(
      `/social-interaction/user/${userId}/profile`,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async listUserProfiles(
    request?: ListUserProfilesRequest,
  ): Promise<ListUserProfilesResponse> {
    const params = new URLSearchParams();
    if (request?.limit !== undefined)
      params.append("limit", request.limit.toString());
    if (request?.offset !== undefined)
      params.append("offset", request.offset.toString());
    if (request?.displayName)
      params.append("display_name", request.displayName);

    const query = params.toString();
    const url = query
      ? `/social-interaction/profiles?${query}`
      : "/social-interaction/profiles";

    const res = await this.api.request<FinalResponse<ListUserProfilesResponse>>(
      url,
      {
        method: "GET",
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async updateUserProfile(
    profileId: string,
    data: UpdateUserProfileRequest,
  ): Promise<UserProfileResponse> {
    const res = await this.api.request<FinalResponse<UserProfileResponse>>(
      `/social-interaction/profiles/${profileId}`,
      {
        method: "PUT",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async updateUserProfileDisplayName(
    profileId: string,
    data: UpdateDisplayNameRequest,
  ): Promise<UserProfileResponse> {
    const res = await this.api.request<FinalResponse<UserProfileResponse>>(
      `/social-interaction/profiles/${profileId}/display-name`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      },
    );
    return this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async softDeleteUserProfile(profileId: string): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      `/social-interaction/profiles/${profileId}`,
      {
        method: "DELETE",
      },
    );
    this.unwrap(res, SocialInteractionService.SERVICE);
  }

  // ==================== Analytics ====================

  async viewedContent(data: ViewedContentRequest): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      "/social-interaction/content/viewed",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async clickedContent(data: ClickContentRequest): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      "/social-interaction/content/clicked",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    this.unwrap(res, SocialInteractionService.SERVICE);
  }

  async openedContent(data: OpenedContentRequest): Promise<void> {
    const res = await this.api.request<FinalResponse<void>>(
      "/social-interaction/content/opened",
      {
        method: "POST",
        body: JSON.stringify(data),
      },
    );
    this.unwrap(res, SocialInteractionService.SERVICE);
  }
}
